import type { SupportedStateCode } from '@/common/constants/states';
import type { ContactsRequiredFields } from '../shared/schema';

/**
 * Per-state behavior of the Listing contacts form. Mirrors the LOM
 * `formConfig` pattern (a flat `DEFAULT_CONFIG` plus per-state deltas). Knobs
 * are derived from the legacy cover sheet listing contact overrides in
 * `views/Coversheet/configurations/listing/overrides/*`.
 *
 * For a listing the user represents the SELLER, so the own-side `agent` is the
 * seller's agent and `client` is the seller; the counterparty side is the
 * buyer. Own-side attorney → `attorney`; counterparty attorney →
 * `counterpartyAttorney`.
 */
export interface ListingContactsConfig {
  // Seller (own) side
  showSellersAttorney: boolean;
  /** Brokerage MLS code on the agent card. */
  showSellerAgentMlsCode: boolean;
  /** Extended agent MLS/brokerage identifiers beyond the shared license block. */
  showSellerAgentOfficeEmail: boolean;
  showSellerAgentPrimaryMls: boolean;
  showSellerAgentMlsId: boolean;
  showSellerAgentBrokerageMlsId: boolean;
  showSellerIntermediaryBanner: boolean;

  // Buyer (counterparty) side
  showBuyersAttorney: boolean;
  showBuyerAgentMlsCode: boolean;
  showBuyerAgentOfficeEmail: boolean;
  showBuyerAgentPrimaryMls: boolean;
  showBuyerAgentMlsId: boolean;
  showBuyerAgentBrokerageMlsId: boolean;
  showBuyerIntermediaryBanner: boolean;
  /** Adds an "Ownership Status" select to the buyer card. */
  showBuyerOwnershipStatus: boolean;

  // Both client cards
  /**
   * Label overrides for the power-of-attorney signer's name fields. Unset keys
   * fall back to the defaults in `ClientFields`; some markets label them
   * "Authorized Signer …".
   */
  customFieldLabels?: {
    poaSignerFirstName?: string;
    poaSignerLastName?: string;
  };

  // Attorney cards
  /**
   * Empty buyer's/seller's attorney cards seeded when absent, so the form opens
   * an editable card instead of a bare "Add" button.
   */
  minAttorneyCards: number;

  // Additional contacts
  showTitleOfficer: boolean;
  /** When false, the title officer card omits the Title Number/GFN field. */
  showTitleNumber: boolean;
  showEscrowOfficer: boolean;
  showAttorneySection: boolean;
  showClosingAttorneySection: boolean;
  showPropertyManagingAgentSection: boolean;
}

const DEFAULT_CONFIG: ListingContactsConfig = {
  showSellersAttorney: false,
  showSellerAgentMlsCode: false,
  showSellerAgentOfficeEmail: false,
  showSellerAgentPrimaryMls: false,
  showSellerAgentMlsId: false,
  showSellerAgentBrokerageMlsId: false,
  showSellerIntermediaryBanner: false,

  showBuyersAttorney: false,
  showBuyerAgentMlsCode: false,
  showBuyerAgentOfficeEmail: false,
  showBuyerAgentPrimaryMls: false,
  showBuyerAgentMlsId: false,
  showBuyerAgentBrokerageMlsId: false,
  showBuyerIntermediaryBanner: false,
  showBuyerOwnershipStatus: false,

  minAttorneyCards: 0,

  showTitleOfficer: true,
  showTitleNumber: true,
  showEscrowOfficer: false,
  showAttorneySection: true,
  showClosingAttorneySection: false,
  showPropertyManagingAgentSection: false,
};

export type ListingContactsConfigDelta = Partial<ListingContactsConfig>;

const AUTHORIZED_SIGNER_LABELS = {
  poaSignerFirstName: 'Authorized Signer First Name',
  poaSignerLastName: 'Authorized Signer Last Name',
};

const STATE_CONFIGS: Partial<
  Record<SupportedStateCode, ListingContactsConfigDelta>
> = {
  CA: { showEscrowOfficer: true },
  FL: { customFieldLabels: AUTHORIZED_SIGNER_LABELS, showTitleNumber: false },
  GA: {
    showSellerAgentMlsCode: true,
    showBuyerAgentMlsCode: true,
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  KY: { customFieldLabels: AUTHORIZED_SIGNER_LABELS },
  MA: {
    showSellersAttorney: true,
    showBuyersAttorney: true,
    customFieldLabels: AUTHORIZED_SIGNER_LABELS,
    showTitleOfficer: false,
    showAttorneySection: false,
  },
  NC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  NJ: {
    showBuyersAttorney: true,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
  },
  NY: {
    showSellersAttorney: true,
    showBuyersAttorney: true,
    minAttorneyCards: 1,
    showTitleOfficer: false,
    showAttorneySection: false,
    showPropertyManagingAgentSection: true,
  },
  OH: { customFieldLabels: AUTHORIZED_SIGNER_LABELS },
  OR: { showTitleOfficer: false, showEscrowOfficer: true },
  SC: {
    showTitleOfficer: false,
    showAttorneySection: false,
    showClosingAttorneySection: true,
  },
  TX: {
    showSellerIntermediaryBanner: true,
    showBuyerIntermediaryBanner: true,
    customFieldLabels: AUTHORIZED_SIGNER_LABELS,
  },
  WA: {
    showEscrowOfficer: true,
    showSellerAgentOfficeEmail: true,
    showSellerAgentPrimaryMls: true,
    showSellerAgentMlsId: true,
    showSellerAgentBrokerageMlsId: true,
    showBuyerAgentOfficeEmail: true,
    showBuyerAgentPrimaryMls: true,
    showBuyerAgentMlsId: true,
    showBuyerAgentBrokerageMlsId: true,
    showBuyerOwnershipStatus: true,
    customFieldLabels: AUTHORIZED_SIGNER_LABELS,
  },
};

export const buildListingContactsConfig = (
  delta: ListingContactsConfigDelta = {},
): ListingContactsConfig => ({ ...DEFAULT_CONFIG, ...delta });

export const resolveListingContactsConfig = (
  state: SupportedStateCode,
): ListingContactsConfig => buildListingContactsConfig(STATE_CONFIGS[state]);

/**
 * The listing contacts' required name/email rules. Single source shared by the
 * validation schema (sidebar badge + lockdown) and the form's asterisk/
 * highlight. The user represents the seller, so the seller's agent + seller are
 * required; the buyer (counterparty) side is not. Listings have no per-state
 * required-ness differences, so this is a constant.
 */
export const listingContactRequiredFields: ContactsRequiredFields = {
  agent: { firstName: true, lastName: true, email: true },
  counterpartyAgent: { firstName: false, lastName: false, email: false },
  client: { firstName: true, lastName: true, email: true },
  counterpartyClient: { firstName: false, lastName: false, email: false },
};
